import {
	ExpandedState,
	flexRender,
	getCoreRowModel,
	getExpandedRowModel,
	getPaginationRowModel,
	getSortedRowModel,
	PaginationState,
	Row,
	RowSelectionState,
	SortingState,
	Updater,
	useReactTable,
} from '@tanstack/react-table';
import { memo, useState } from 'react';
import React from 'react';
import { useTheme } from 'styled-components';

import SortSVG from '@/shared/assets/icons/ui-kit/sort.svg';
import { SortOrder } from '@/shared/constants/sort-order.constant';
import { useDelayedLoading } from '@/shared/lib/hooks/use-delayed-loading.hook';

import { Box } from '../box';
import { Icon } from '../icon';
import { ListPagination } from '../list-pagination';
import { Pagination } from '../pagination';
import { TanstackTableProps } from './table.types';
import { getSortingState } from './tanstack-table.helpers';
import * as Styled from './tanstack-table.styles';
import { SkeletonRow } from './ui/skeleton-row';

const TanstackTable = <T,>({
	data,
	columns,
	getRowId,
	getSubRows,
	expanded: controlledExpanded,
	onExpandedChange: onExpandedChangeProp,
	onRowClick,
	rowSelection: controlledRowSelection,
	onRowSelectionChange,
	width = '100%',
	scrollHeight,
	align = 'left',
	cellPadding = '16px 32px',
	background,
	headBackground,
	borderColor,
	showRowDivider,
	headColor,
	headWhiteSpace,
	bordered,
	zIndexHeader = 1,
	highlightRow,
	groupedData,
	expandedContent,
	pageSize = 50,
	onChangePage,
	totalPages = 1,
	page = 1,
	showPagination = true,
	hideSelectPageSize = false,
	onPageSizeChange,
	total,
	filters,
	enableSorting = false,
	onChangeSort,
	sort,
	manualPagination = true,
	manualSorting = true,
	noDataText = 'No results found',
	isLoading = false,
	skeletonRowsCount,
	pageSizeOptions,
	pagesCutCount,
}: TanstackTableProps<T>) => {
	const {
		table: { headBackground: defaultHeadBackground, headColor: defaultHeadColor, zebraBackground },
	} = useTheme();

	const pageIndex = page - 1;

	const isLoadingVisible = useDelayedLoading(isLoading);
	const [internalExpanded, setInternalExpanded] = useState<ExpandedState>({});
	const [internalSorting, setInternalSorting] = useState<SortingState>([]);
	const [internalRowSelection, setInternalRowSelection] = useState<RowSelectionState>({});
	const expanded = controlledExpanded ?? internalExpanded;
	const sorting = getSortingState(internalSorting, sort);
	const rowSelection = controlledRowSelection ?? internalRowSelection;

	const table = useReactTable({
		data,
		columns,
		getRowId,
		getCoreRowModel: getCoreRowModel(),
		autoResetPageIndex: false,
		manualPagination,
		manualSorting,
		enableRowSelection: true,
		getSubRows,
		state: { pagination: { pageIndex, pageSize }, expanded, sorting, rowSelection },
		onExpandedChange: (updater) => {
			if (!controlledExpanded) {
				setInternalExpanded(updater);
			}

			onExpandedChangeProp?.(updater);
		},
		onRowSelectionChange: (updater) => {
			if (!controlledRowSelection) {
				setInternalRowSelection(updater);
			}

			onRowSelectionChange?.(updater);
		},
		getExpandedRowModel: getExpandedRowModel(),
		...(!manualPagination &&
			showPagination && {
				getPaginationRowModel: getPaginationRowModel(),
			}),
		...(!manualSorting &&
			enableSorting && {
				getSortedRowModel: getSortedRowModel(),
			}),
		...(enableSorting && {
			onSortingChange: (updater) => {
				const newSorting = typeof updater === 'function' ? updater(sorting) : updater;

				setInternalSorting(newSorting);

				const sort = newSorting[0];

				onChangeSort?.({
					field: sort?.id ?? null,
					direction: sort ? (sort.desc ? SortOrder.DESC : SortOrder.ASC) : null,
				});
			},
		}),
		...(showPagination && {
			onPaginationChange: (updater: Updater<PaginationState>) => {
				if (isLoading) return;

				const newPagination: PaginationState =
					typeof updater === 'function' ? updater({ pageIndex, pageSize }) : updater;

				onChangePage?.(newPagination.pageIndex + 1);
			},
		}),
	});

	const hasVisibleRows = table.getRowModel().rows.length > 0;
	const resolvedSkeletonRowsCount = Math.max(1, skeletonRowsCount ?? pageSize);

	const renderRow = (rowModel: Row<T>, index: number) => {
		const isExpanded = rowModel.getIsExpanded();
		const backgroundColor = highlightRow?.(rowModel.original) || (!(index % 2 === 0) && zebraBackground) || '';
		const shouldShowDivider = showRowDivider?.(rowModel.original);
		const handleRowClick = () => onRowClick?.(rowModel.original);

		return (
			<React.Fragment key={rowModel.id}>
				<Styled.TableRow
					background={background}
					backgroundColor={backgroundColor}
					onClick={handleRowClick}
					clickable={Boolean(onRowClick)}
				>
					{rowModel.getVisibleCells().map((cell) => (
						<Styled.TableCell key={cell.id} cellPadding={cellPadding} borderColor={borderColor}>
							{flexRender(cell.column.columnDef.cell, cell.getContext())}

							{/* dashed box */}
							{shouldShowDivider && <Styled.RowDashedBox />}
						</Styled.TableCell>
					))}
				</Styled.TableRow>

				{isExpanded && expandedContent && (
					<Styled.TableRow background={background}>
						<Styled.TableCell
							colSpan={table.getAllLeafColumns().length}
							cellPadding={cellPadding}
							borderColor={borderColor}
						>
							{expandedContent(rowModel.original)}
						</Styled.TableCell>
					</Styled.TableRow>
				)}
			</React.Fragment>
		);
	};

	return (
		<Box direction="column" width={width} justify="space-between">
			{filters}

			{!hasVisibleRows && !isLoading && <Styled.EmptyState>{noDataText}</Styled.EmptyState>}

			{(hasVisibleRows || isLoadingVisible) && (
				<Styled.TableWrapper
					bordered={bordered}
					scrollHeight={scrollHeight}
					background={background}
					borderColor={borderColor}
				>
					<Styled.Table width={width} background={background}>
						<Styled.TableHead zIndex={zIndexHeader}>
							{table.getHeaderGroups().map((headerGroup) => (
								<tr key={headerGroup.id}>
									{headerGroup.headers.map((header) => (
										<Styled.TableHeader
											key={header.id}
											cellPadding={cellPadding}
											align={align}
											background={background}
											headBackground={headBackground ?? defaultHeadBackground}
											headColor={headColor ?? defaultHeadColor}
											headWhiteSpace={headWhiteSpace}
											onClick={
												enableSorting && header.column.getCanSort()
													? header.column.getToggleSortingHandler()
													: undefined
											}
										>
											<Box direction="row" align="center" gap={4}>
												{flexRender(header.column.columnDef.header, header.getContext())}
												{enableSorting && header.column.getCanSort() && (
													<Icon Svg={SortSVG} fill={headColor ?? defaultHeadColor} cursor="pointer" />
												)}
											</Box>
										</Styled.TableHeader>
									))}
								</tr>
							))}
						</Styled.TableHead>
						<tbody>
							{isLoadingVisible
								? Array.from({ length: resolvedSkeletonRowsCount }).map((_, index) => (
										<SkeletonRow
											key={`skeleton-${index}`}
											columnCount={table.getAllLeafColumns().length}
											cellPadding={cellPadding}
										/>
								  ))
								: groupedData
								? Object.entries(groupedData).map(([groupTitle, groupRows]) => (
										<React.Fragment key={groupTitle}>
											{/* Group title row */}
											{groupTitle && (
												<tr>
													<Styled.GroupTitle
														cellPadding={cellPadding}
														colSpan={table.getAllColumns().length}
														background={background}
														headBackground={headBackground ?? defaultHeadBackground}
														headColor={headColor}
														borderColor={borderColor}
													>
														{groupTitle}
													</Styled.GroupTitle>
												</tr>
											)}

											{/* Group rows */}
											{groupRows.map((row, index) => {
												const rowModel = table.getRowModel().rows.find((r) => r.original === row);

												return rowModel ? renderRow(rowModel, index) : null;
											})}
										</React.Fragment>
								  ))
								: table.getRowModel().rows.map(renderRow)}
						</tbody>
					</Styled.Table>
				</Styled.TableWrapper>
			)}

			{/* Pagination */}
			{showPagination && hasVisibleRows && total && onChangePage && (
				<Styled.PaginationWrapper isLoading={isLoadingVisible}>
					{!hideSelectPageSize && (
						<>
							<ListPagination
								pageSizeOptions={pageSizeOptions}
								pageSize={pageSize}
								onPageSizeChange={(size) => {
									if (isLoading) return;

									onPageSizeChange?.(size);
								}}
								total={total}
							/>
							<Styled.PaginationDivider aria-hidden="true">|</Styled.PaginationDivider>
						</>
					)}
					<Pagination
						hasRowsPerPage={!hideSelectPageSize}
						pagesCutCount={pagesCutCount}
						page={page}
						totalPages={totalPages}
						onChangePage={(newPage) => {
							if (isLoading) return;

							table.setPageIndex(newPage - 1);
						}}
					/>
				</Styled.PaginationWrapper>
			)}
		</Box>
	);
};

export default memo(TanstackTable) as typeof TanstackTable;
